/**
 * OwnerTabs - Standardized tab navigation component
 * Matches Calendar page interaction patterns exactly
 * 
 * Contract:
 * - 36px visual height / 40px minimum hit target
 * - cubic-bezier(0.4, 0, 0.2, 1) easing
 * - rgba(59, 130, 246, 0.04) hover state
 * - CSS variable-based focus states
 */
import React from 'react';

export default function OwnerTabs({ activeKey, onChange, tabs = [], variant }) {
  if (!tabs || tabs.length === 0) {
    return null;
  }

  const isLedger = variant === 'ledger';
  const containerStyle = isLedger ? styles.ledgerTabsContainer : styles.tabsContainer;
  const tabStyle = isLedger ? styles.ledgerTab : styles.tab;
  const tabActiveStyle = isLedger ? styles.ledgerTabActive : styles.tabActive;

  return (
    <div style={containerStyle}>
      {tabs.map((tab) => {
        const isActive = activeKey === tab.key;
        return (
          <button
            key={tab.key}
            style={{
              ...tabStyle,
              ...(isActive ? tabActiveStyle : {}),
            }}
            onClick={() => onChange && onChange(tab.key)}
            onMouseEnter={(e) => {
              if (!isActive) {
                e.currentTarget.style.backgroundColor = isLedger ? 'rgba(0, 0, 0, 0.04)' : 'rgba(59, 130, 246, 0.04)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isActive) {
                e.currentTarget.style.backgroundColor = 'transparent';
              }
            }}
            onFocus={(e) => {
              e.currentTarget.style.outline = 'var(--focus-outline-width) var(--focus-outline-style) var(--focus-outline-color)';
              e.currentTarget.style.outlineOffset = 'var(--focus-outline-offset)';
            }}
            onBlur={(e) => {
              e.currentTarget.style.outline = 'none';
            }}
            aria-selected={isActive}
            role="tab"
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

const styles = {
  // Tab navigation: align with Calendar page interaction standards
  tabsContainer: {
    display: 'flex',
    gap: 'var(--spacing-2)',
    marginBottom: 0,
    borderBottom: '2px solid var(--color-border-medium, #d1d5db)',
    paddingBottom: 'var(--spacing-2)',
    backgroundColor: 'var(--color-bg-primary, #ffffff)',
    paddingLeft: 'var(--spacing-4, 16px)',
    paddingRight: 'var(--spacing-4, 16px)',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
  },
  tab: {
    appearance: 'none',
    border: 'none',
    background: 'transparent',
    height: '36px', // Match Calendar button height (accessibility: 36px + 2px padding = 38px effective)
    minHeight: '36px',
    minWidth: '40px', // Ensure minimum 40px hit target for accessibility
    padding: '0 var(--spacing-4)',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-medium)',
    color: 'var(--color-text-secondary)',
    cursor: 'pointer',
    transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)', // Premium easing: match Calendar standard
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    whiteSpace: 'nowrap',
    outline: 'none',
    position: 'relative',
  },
  tabActive: {
    backgroundColor: 'var(--color-brand-primary, #3b82f6)',
    color: '#ffffff',
    fontWeight: 'var(--font-weight-semibold)',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
  ledgerTabsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 'var(--spacing-1, 4px)',
    alignItems: 'center',
    padding: 'var(--spacing-1, 4px)',
    backgroundColor: 'var(--color-bg-tertiary, #f3f4f6)',
    border: '1px solid var(--color-border-light, #e5e7eb)',
    borderRadius: 'var(--radius-md, 6px)',
    minWidth: 0,
  },
  ledgerTab: {
    appearance: 'none',
    border: 'none',
    background: 'transparent',
    height: '34px',
    minHeight: '34px',
    minWidth: '36px',
    padding: '0 var(--spacing-3, 12px)',
    borderRadius: 'var(--radius-sm, 4px)',
    fontSize: 'var(--font-size-sm, 14px)',
    fontWeight: 'var(--font-weight-medium, 500)',
    color: 'var(--color-text-secondary, #6b7280)',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease, color 0.15s ease',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    whiteSpace: 'nowrap',
    outline: 'none',
    position: 'relative',
  },
  ledgerTabActive: {
    backgroundColor: 'var(--color-bg-primary, #ffffff)',
    color: 'var(--color-text-primary, #111827)',
    fontWeight: 'var(--font-weight-semibold, 600)',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.06)',
  },
};
